import { ExternalLink, FolderOpen, Loader2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DocumentPreviewModal } from '@/components/documents/DocumentPreviewModal';
import { DocumentStatusBadge } from '@/components/documents/DocumentStatusBadge';
import { VerificationControls } from '@/components/documents/VerificationControls';
import {
  getDocumentsForApplication,
  rejectDocument,
  requestReupload,
  verifyDocument,
} from '@/services/documentService';

import type { HrDocument } from '@/types/document.types';
import type { SubmissionDetail } from '@/types/submission.types';

interface DocumentsTabProps {
  applicationId: string;
  onDocumentsLoaded?: (docs: HrDocument[]) => void;
  applicationData?: SubmissionDetail;
}

function formatTypeName(type: string): string {
  return type
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function formatDate(dateStr?: string): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function DocumentsTab({ applicationId, onDocumentsLoaded, applicationData }: DocumentsTabProps) {
  const { t } = useTranslation();
  const [documents, setDocuments] = useState<HrDocument[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [previewDocumentId, setPreviewDocumentId] = useState<string | null>(null);

  const loadDocuments = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const docs = await getDocumentsForApplication(applicationId);
      setDocuments(docs);
      onDocumentsLoaded?.(docs);
    } catch {
      setError(t('documents.errorLoading'));
    } finally {
      setIsLoading(false);
    }
  }, [applicationId, onDocumentsLoaded, t]);

  useEffect(() => {
    void loadDocuments();
  }, [loadDocuments]);

  const handleVerify = async (id: string) => {
    await verifyDocument(id);
    await loadDocuments();
  };

  const handleReject = async (id: string, reason: string) => {
    await rejectDocument(id, reason);
    await loadDocuments();
  };

  const handleReupload = async (id: string, reason: string) => {
    await requestReupload(id, reason);
    await loadDocuments();
  };

  if (isLoading) {
    return (
      <div className='flex items-center justify-center min-h-[200px]'>
        <Loader2 className='h-8 w-8 animate-spin text-blue-600' />
      </div>
    );
  }

  if (error) {
    return <div className='bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700'>{error}</div>;
  }

  if (documents.length === 0) {
    return (
      <div className='bg-white shadow rounded-lg p-12 flex flex-col items-center justify-center text-center'>
        <FolderOpen className='h-12 w-12 text-gray-300 mb-4' />
        <h3 className='text-lg font-medium text-gray-600 mb-2'>{t('submissionDetail.documents.title')}</h3>
        <p className='text-sm text-gray-400'>{t('submissionDetail.documents.noDocuments')}</p>
      </div>
    );
  }

  // Group documents by category
  const grouped = new Map<string, HrDocument[]>();
  for (const doc of documents) {
    const existing = grouped.get(doc.category) ?? [];
    existing.push(doc);
    grouped.set(doc.category, existing);
  }

  return (
    <div className='space-y-6'>
      {Array.from(grouped.entries()).map(([category, docs]) => (
        <div key={category} className='bg-white shadow rounded-lg overflow-hidden'>
          <div className='px-6 py-4 bg-gray-50 border-b border-gray-200'>
            <h4 className='text-sm font-semibold text-gray-700 uppercase tracking-wide'>{formatTypeName(category)}</h4>
          </div>

          <div className='divide-y divide-gray-100'>
            {docs.map((doc) => (
              <div key={doc.documentId} className='px-6 py-4'>
                <div className='flex items-start justify-between gap-4'>
                  <div className='min-w-0 flex-1'>
                    <div className='flex items-center gap-3 mb-1'>
                      <span className='text-sm font-medium text-gray-900 truncate'>{doc.filename}</span>
                      <DocumentStatusBadge status={doc.status} />
                    </div>
                    <div className='flex items-center gap-4 text-xs text-gray-500'>
                      <span>{formatTypeName(doc.type)}</span>
                      <span>
                        {t('documents.columns.uploadedAt')}: {formatDate(doc.uploadedAt)}
                      </span>
                    </div>
                    {doc.rejectionReason ? <p className='mt-2 text-xs text-red-600'>{doc.rejectionReason}</p> : null}
                  </div>

                  <div className='flex items-center gap-2 flex-shrink-0'>
                    <button
                      type='button'
                      onClick={() => setPreviewDocumentId(doc.documentId)}
                      className='inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md bg-gray-50 text-gray-700 hover:bg-gray-100'>
                      <ExternalLink className='h-3.5 w-3.5' />
                      {t('documents.actions.preview')}
                    </button>
                    <VerificationControls
                      documentId={doc.documentId}
                      status={doc.status}
                      onVerify={handleVerify}
                      onReject={handleReject}
                      onReupload={handleReupload}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {previewDocumentId ? (
        <DocumentPreviewModal
          documentId={previewDocumentId}
          isOpen
          onClose={() => setPreviewDocumentId(null)}
          onStatusChange={() => void loadDocuments()}
          applicationData={applicationData}
        />
      ) : null}
    </div>
  );
}
